import { v4 as uuidv4 } from 'uuid'
import Indicator from '../condition/indicators/types/Indicator'
import {
  CompositeExpr,
  CompositeNode,
  CompositeRef,
  CompositeSourceRef,
  SourceField,
} from '../../../types/composite'

/**
 * Editor-side step model. Ids are stable uuids so refs survive edits;
 * canonicalizeDef renumbers them to n1..nk at publish time.
 */
export type DraftStep = {
  id: string
  kind: 'indicator' | 'math'
  indicatorName?: string
  params?: Record<string, any>
  input?: CompositeRef
  expr?: CompositeExpr
}

export const newIndicatorStep = (): DraftStep => ({ id: uuidv4(), kind: 'indicator' })

export const newMathStep = (): DraftStep => ({
  id: uuidv4(),
  kind: 'math',
  expr: { t: 'const', value: 0 },
})

export const draftToNodes = (steps: DraftStep[]): CompositeNode[] =>
  steps.map((step) => {
    if (step.kind === 'indicator') {
      const params = { ...(step.params ?? {}) }
      // A series input supersedes the indicator's own price-field param; a def
      // carrying both would be contradictory for the future evaluators.
      if (step.input) delete params.field
      const node: CompositeNode = {
        id: step.id,
        kind: 'indicator',
        name: step.indicatorName ?? '',
        params,
      }
      if (step.input) node.input = step.input
      return node
    }
    return { id: step.id, kind: 'math', expr: step.expr ?? { t: 'const', value: 0 } }
  })

// Pre-source-removal defs declared chart taps as dedicated 'source' nodes;
// tolerate them by folding each into direct source refs on its consumers.
type LegacyDefNode = CompositeNode | { id: string; kind: 'source'; field?: SourceField }

export const nodesToDraft = (nodes: LegacyDefNode[]): DraftStep[] => {
  const legacySources = new Map<string, SourceField>()
  for (const node of nodes) {
    if (node.kind === 'source') legacySources.set(node.id, node.field ?? 'close')
  }
  const fixRef = (ref: CompositeRef | undefined): CompositeRef | undefined => {
    if (!ref || !('node' in ref)) return ref
    const field = legacySources.get(ref.node)
    if (field === undefined) return ref
    const source: CompositeSourceRef = { source: field }
    if (ref.offset) source.offset = ref.offset
    return source
  }
  const fixExpr = (expr: CompositeExpr): CompositeExpr => {
    if (expr.t === 'const') return expr
    if (expr.t === 'ref') return { t: 'ref', ref: fixRef(expr.ref)! }
    return { t: 'op', op: expr.op, args: expr.args.map(fixExpr) }
  }
  return nodes
    .filter((node): node is CompositeNode => node.kind !== 'source')
    .map((node) => {
      if (node.kind === 'indicator') {
        const step: DraftStep = {
          id: node.id,
          kind: 'indicator',
          indicatorName: node.name,
          params: { ...node.params },
        }
        const input = fixRef(node.input)
        if (input) step.input = input
        return step
      }
      return { id: node.id, kind: 'math', expr: fixExpr(node.expr) }
    })
}

const SOURCE_LABELS: Record<SourceField, string> = {
  open: 'Open',
  high: 'High',
  low: 'Low',
  close: 'Close',
  hl2: 'HL/2',
  hlc3: 'HLC/3',
  ohlc4: 'OHLC/4',
  volume: 'Volume',
}

export const sourceFieldLabel = (field: SourceField): string => SOURCE_LABELS[field]

export const SOURCE_FIELDS = Object.keys(SOURCE_LABELS) as SourceField[]

/** ①-style step marker for compact labels (falls back past ⑫, but maxNodes caps at 12). */
export const stepMarker = (index: number): string =>
  index < 20 ? String.fromCharCode(0x2460 + index) : `#${index + 1}`

export const outputLabel = (output: string): string => {
  const spaced = output.replace(/([A-Z])/g, ' $1')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export const stepLabel = (
  step: DraftStep,
  index: number,
  getIndicatorByValue: (value: string) => Indicator | undefined
): string => {
  let description = 'Math'
  if (step.kind === 'indicator') {
    const entry = step.indicatorName ? getIndicatorByValue(step.indicatorName) : undefined
    const name = entry ? (entry.nickname ?? entry.label) : 'Indicator'
    const period = typeof step.params?.period === 'number' ? ` (${step.params.period})` : ''
    description = `${name}${period}`
  }
  return `${stepMarker(index)} ${description}`
}

const refText = (ref: CompositeRef | undefined, steps: DraftStep[]): string => {
  if (!ref) return ''
  if (!('node' in ref)) {
    const back = ref.offset ? ` [${ref.offset} back]` : ''
    return `${sourceFieldLabel(ref.source)}${back}`
  }
  const i = steps.findIndex((s) => s.id === ref.node)
  const base = i >= 0 ? `Step ${i + 1}` : 'Step ?'
  const output = ref.output ? ` ${ref.output}` : ''
  const back = ref.offset ? ` [${ref.offset} back]` : ''
  return `${base}${output}${back}`
}

const OP_SYMBOLS: Record<string, string> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷',
  '>': '>',
  '<': '<',
  '>=': '≥',
  '<=': '≤',
  and: 'and',
  or: 'or',
}

export const exprText = (expr: CompositeExpr, steps: DraftStep[]): string => {
  if (expr.t === 'const') return Number.isFinite(expr.value) ? String(expr.value) : '…'
  if (expr.t === 'ref') return refText(expr.ref, steps)
  const args = expr.args.map((arg) => {
    const text = exprText(arg, steps)
    return arg.t === 'op' ? `(${text})` : text
  })
  if (expr.op === 'abs') return `|${args[0] ?? ''}|`
  if (expr.op === 'neg') return `−${args[0] ?? ''}`
  if (expr.op === 'not') return `not ${args[0] ?? ''}`
  if (expr.op === 'min' || expr.op === 'max') return `${expr.op}(${args.join(', ')})`
  return args.join(` ${OP_SYMBOLS[expr.op] ?? expr.op} `)
}

/** Condition-label-style detail text for a step header — the part after the
 * "Kind:" prefix (e.g. "Close", "Simple Moving Average (14) · Step 1", "4 + 4"). */
export const stepSummary = (
  step: DraftStep,
  steps: DraftStep[],
  getIndicatorByValue: (value: string) => Indicator | undefined
): string => {
  if (step.kind === 'indicator') {
    const entry = step.indicatorName ? getIndicatorByValue(step.indicatorName) : undefined
    if (!entry) return 'select an indicator'
    const period =
      typeof step.params?.period === 'number' && Number.isFinite(step.params.period)
        ? ` (${step.params.period})`
        : ''
    const input = step.input ? ` · ${refText(step.input, steps)}` : ''
    return `${entry.label}${period}${input}`
  }
  return exprText(step.expr ?? { t: 'const', value: 0 }, steps)
}
